import { Component, OnDestroy, OnInit } from '@angular/core';
import { ServiceDeliveryLocationsFacade } from '../../../services/service-delivery-locations.facade';
import { PopSummary } from '../../../services/service-delivery-locations.service';
import { CompanySummary } from '../../../models/company-summary';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type PopViewMode = 'list' | 'map';

@Component({
  selector: 'app-service-delivery-locations-panel',
  templateUrl: './service-delivery-locations-panel.component.html',
  styleUrls: ['./service-delivery-locations-panel.component.scss']
})
export class ServiceDeliveryLocationsPanelComponent implements OnInit, OnDestroy {
  selectedCompany: CompanySummary | null = null;
  companyPops: PopSummary[] = [];
  mappablePops: PopSummary[] = [];
  selectedPopId: number | null = null;
  loadingPops = false;
  popViewMode: PopViewMode = 'list';
  private readonly expandedPopIds = new Set<number>();
  collapsedListView = false;
  collapsedMapView = false;
  private hoverRestoreList = false;
  private hoverRestoreMap = false;
  private lastCompanyId: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly facade: ServiceDeliveryLocationsFacade
  ) {}

  ngOnInit(): void {
    this.facade.selectedCompany$
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        const currentCompanyId = company?.id ?? null;
        const didChange = currentCompanyId !== this.lastCompanyId;
        this.selectedCompany = company;
        this.lastCompanyId = currentCompanyId;
        if (!company) {
          this.companyPops = [];
          this.mappablePops = [];
        }
        if (didChange) {
          this.resetViewState();
        }
      });

    this.facade.companyPops$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pops => {
        this.companyPops = this.sortPops(pops);
      });

    this.facade.mappablePops$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pops => {
        this.mappablePops = pops;
        if (this.popViewMode === 'map' && this.mappablePops.length === 0) {
          this.popViewMode = 'list';
        }
      });

    this.facade.selectedPop$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pop => {
        this.selectedPopId = pop?.id ?? null;
      });

    this.facade.companyPopsLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => this.loadingPops = loading);
  }

  private resetViewState(): void {
    this.popViewMode = 'list';
    this.collapsedListView = false;
    this.collapsedMapView = false;
    this.hoverRestoreList = false;
    this.hoverRestoreMap = false;
    this.expandedPopIds.clear();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChangeViewMode(mode: PopViewMode): void {
    if (mode === this.popViewMode) {
      this.toggleActiveView(mode);
      return;
    }
    if (mode === 'map' && this.mappablePops.length === 0) {
      return;
    }
    this.popViewMode = mode;
    this.setCollapsedState(mode, false);
  }

  onSelectPop(pop: PopSummary, origin: PopViewMode, shouldScroll = false): void {
    this.facade.selectPop(pop);
    this.setCollapsedState(origin, true);
    if (!shouldScroll) {
      this.ensurePanelVisible(origin);
    }
    if (!shouldScroll) {
      return;
    }
    const scrollDelay = this.getScrollDelay(origin);
    setTimeout(() => {
      const detailsSection = document.querySelector('.details-section');
      if (!detailsSection) {
        return;
      }
      const rect = detailsSection.getBoundingClientRect();
      const offset = 80;
      const target = Math.max(window.scrollY + rect.top - offset, 0);
      window.scrollTo({ top: target, behavior: 'smooth' });
    }, scrollDelay);
  }

  serviceLabel(service: string): string {
    switch (service.toLowerCase()) {
      case 'qed':
        return 'Snapshot (QED)';
      case 'qrd':
        return 'Raw (QRD)';
      case 'qfd':
        return 'Price & Volume Signals (QFD)';
      case 'qhf':
        return 'Volume Signals (QHF)';
      case 'news':
        return 'News';
      case 'time_sync_as_a_service':
        return 'Precision Time Exchange';
      case 'bandwidth':
        return 'Bandwidth';
      default:
        return service.toUpperCase();
    }
  }

  getServiceChipClass(service: string): string {
    return (service || '').toLowerCase() === 'bandwidth' ? 'service-chip--bandwidth' : 'service-chip--market';
  }

  getPopCrossConnectLinks(pop: PopSummary): { id: string, label: string }[] {
    if (!pop.crossConnects?.length) {
      return [];
    }

    const links: { id: string, label: string }[] = [];
    const seen = new Set<string>();

    pop.crossConnects.forEach(cc => {
      if (!cc.crossConnectId) {
        return;
      }
      if (!seen.has(cc.crossConnectId)) {
        seen.add(cc.crossConnectId);
        links.push({ id: cc.crossConnectId, label: cc.crossConnectId });
      }
    });

    return links;
  }

  isPopXcoExpanded(popId: number | null | undefined): boolean {
    if (!popId) {
      return false;
    }
    return this.expandedPopIds.has(popId);
  }

  togglePopXcoExpansion(pop: PopSummary, event: Event): void {
    event.stopPropagation();
    if (!pop?.id) {
      return;
    }
    if (this.expandedPopIds.has(pop.id)) {
      this.expandedPopIds.delete(pop.id);
    } else {
      this.expandedPopIds.add(pop.id);
    }
  }

  private setCollapsedState(mode: PopViewMode, collapsed: boolean): void {
    if (mode === 'list') {
      this.collapsedListView = collapsed;
      if (!collapsed) {
        this.hoverRestoreList = false;
      }
    } else {
      this.collapsedMapView = collapsed;
      if (!collapsed) {
        this.hoverRestoreMap = false;
      }
    }
  }

  private getScrollDelay(origin: PopViewMode): number {
    const collapseDuration = 350;
    if (origin === 'list' && this.collapsedListView) {
      return collapseDuration;
    }
    if (origin === 'map' && this.collapsedMapView) {
      return collapseDuration;
    }
    return 0;
  }

  private toggleActiveView(mode: PopViewMode): void {
    if (mode === 'list') {
      this.collapsedListView = !this.collapsedListView;
    } else {
      this.collapsedMapView = !this.collapsedMapView;
    }
  }

  private ensurePanelVisible(origin: PopViewMode): void {
    if (origin !== 'list') {
      return;
    }
    const panel = document.querySelector('.pops-section');
    if (!panel) {
      return;
    }
    const rect = panel.getBoundingClientRect();
    if (rect.top >= 0) {
      return;
    }
    const target = Math.max(window.scrollY + rect.top - 20, 0);
    window.scrollTo({ top: target, behavior: 'smooth' });
  }

  handlePanelMouseEnter(): void {
    if (this.popViewMode === 'list' && this.collapsedListView) {
      this.hoverRestoreList = true;
      this.collapsedListView = false;
    }
    if (this.popViewMode === 'map' && this.collapsedMapView) {
      this.hoverRestoreMap = true;
      this.collapsedMapView = false;
    }
  }

  handlePanelMouseLeave(): void {
    if (this.hoverRestoreList) {
      this.collapsedListView = true;
      this.hoverRestoreList = false;
    }
    if (this.hoverRestoreMap) {
      this.collapsedMapView = true;
      this.hoverRestoreMap = false;
    }
  }

  chevronClass(mode: PopViewMode): string {
    const isActive = this.popViewMode === mode;
    const isCollapsed = mode === 'list' ? this.collapsedListView : this.collapsedMapView;
    if (!isActive || isCollapsed) {
      return 'chevron chevron--collapsed';
    }
    return 'chevron chevron--expanded';
  }

  private sortPops(pops: PopSummary[]): PopSummary[] {
    return [...pops].sort((a, b) => {
      const left = (a.displayId || '').toUpperCase();
      const right = (b.displayId || '').toUpperCase();
      return left.localeCompare(right);
    });
  }
}
